/**
 * ConditionInsightCard
 *
 * Renders condition-specific education content for the active step.
 * Appears when user has reported conditions (PMOS (ex PCOS), endo, thyroid, etc.).
 * Uses canonical SupplementRow for any recommended supplements.
 */

import React, { useState } from "react";
import { View, Text, Pressable } from "react-native";
import { Collapsible } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { SupplementRow } from "@src/packages/content";
import { useTranslations } from "@src/packages/i18n";
import { usePressCapture } from "@src/packages/analytics/usePressCapture";
import type { ConditionStepContent } from "../intelligence/conditionContent";

type Props = {
  content: ConditionStepContent;
  /** Tap handler for a supplement row — receives the supplement name so
   *  the caller can resolve to a ContentBlock and route into the
   *  Supplements detail sheet. Optional; rows render non-tappable without
   *  it. */
  onPressSupplement?: (name: string) => void;
};

export default function ConditionInsightCard({
  content,
  onPressSupplement,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("ConditionInsightCard");
  const [expanded, setExpanded] = useState(false);

  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as object),
        padding: 16,
      }}
    >
      {/* Title — section header above ("About you") already frames the
          card as a persona-derived fact, so we don't repeat a stamp here. */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "600",
          color: EMBER.colors.text,
          marginBottom: 6,
        }}
      >
        {content.title}
      </Text>

      {/* Education */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "400",
          color: EMBER.colors.textMid,
          lineHeight: 19,
        }}
      >
        {content.education}
      </Text>

      {/* Expandable details */}
      {content.details ? (
        <Pressable
          onPress={() => {
            capturePress("condition_insight_details");
            setExpanded(!expanded);
          }}
          style={{ marginTop: 10 }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "500",
              color: EMBER.colors.warm,
            }}
          >
            {expanded
              ? t("condition_insight.show_less")
              : t("condition_insight.learn_more")}
          </Text>
        </Pressable>
      ) : null}

      {content.details ? (
        <Collapsible expanded={expanded}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "400",
              color: EMBER.colors.textMid,
              lineHeight: 19,
              marginTop: 8,
            }}
          >
            {content.details}
          </Text>
        </Collapsible>
      ) : null}

      {/* Supplement recommendations. SupplementRow keeps its own glass
          shell intentionally — the nested-glass look is the canonical
          treatment. Sub-header uses the SectionTitle vibe (11px upper)
          so the in-card hierarchy stays distinct from the card title. */}
      {content.supplementRecommendations &&
      content.supplementRecommendations.length > 0 ? (
        <View style={{ marginTop: 14, gap: 8 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "600",
              color: EMBER.colors.textLight,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            {t("condition_insight.recommended_supplements")}
          </Text>
          {content.supplementRecommendations.map((supp, idx) => (
            <SupplementRow
              key={`${supp.name}-${idx}`}
              name={supp.name}
              dosage={supp.dosage}
              description={supp.note}
              evidence={supp.evidence}
              onPress={
                onPressSupplement
                  ? () => onPressSupplement(supp.name)
                  : undefined
              }
            />
          ))}
        </View>
      ) : null}
    </View>
  );
}
